import { useCallback, useEffect, useState } from 'react';

export const SIDEBAR_COLLAPSED_KEY = 'sgi-sidebar-collapsed';
export const SIDEBAR_MOBILE_MQ = '(max-width: 991px)';

function readStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Estado do menu lateral: no desktop persiste preferência;
 * no mobile inicia/força recolhido (drawer) ao entrar no breakpoint.
 *
 * O estado inicial é sempre `true` / `isMobile: false` (sem `matchMedia` no
 * initializer) para evitar mismatch SSR/hydration; o efeito sincroniza no mount.
 */
export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const setCollapsedPersist = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setCollapsed((prev) => {
        const value = typeof next === 'function' ? next(prev) : next;
        if (!window.matchMedia(SIDEBAR_MOBILE_MQ).matches) {
          try {
            localStorage.setItem(SIDEBAR_COLLAPSED_KEY, value ? '1' : '0');
          } catch {
            /* ignore */
          }
        }
        return value;
      });
    },
    [],
  );

  const toggle = useCallback(() => {
    setCollapsedPersist((prev) => !prev);
  }, [setCollapsedPersist]);

  const collapse = useCallback(() => {
    setCollapsedPersist(true);
  }, [setCollapsedPersist]);

  useEffect(() => {
    const mq = window.matchMedia(SIDEBAR_MOBILE_MQ);
    const apply = () => {
      const mobile = mq.matches;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      } else {
        setCollapsed(readStoredCollapsed());
      }
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return { collapsed, isMobile, toggle, collapse, setCollapsed: setCollapsedPersist };
}
